import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  Input,
  Label
} from 'reactstrap'
import { getAdditionalMilestonesDetails, exportAdditionalMilestones } from '../../../../helpers/rfq_dashboard_api'

const AdditionalMilestonesModal = ({ 
  isOpen, 
  toggle, 
  filters = {},
  title = "Additional Milestones Details"
}) => {
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  })
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [isOpen, pagination.current_page])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        ...filters,
        page: pagination.current_page,
        per_page: pagination.per_page
      }

      console.log("Fetching additional milestones details:", params)

      const response = await getAdditionalMilestonesDetails(params)

      if (response.status) {
        setData(response.data.milestones || [])
        setPagination(response.data.pagination || pagination)
      } else {
        setError(response.message || 'Failed to fetch additional milestones details')
      }
    } catch (error) {
      console.error('Error fetching additional milestones details:', error)
      setError(error.message || 'An error occurred while fetching data')
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      
      const params = {
        ...filters
      }

      console.log("Exporting additional milestones:", params)

      const response = await exportAdditionalMilestones(params)
      
      if (response.status && response.data) {
        // Create blob and download
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `additional_milestones_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        URL.revokeObjectURL(url)
        
        console.log("Additional milestones export completed successfully")
      } else {
        throw new Error('Export failed - no data received')
      }
    } catch (error) {
      console.error('Export error:', error)
      setError(error.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }))
  }

  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00'
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success'
      case 'in_progress': return 'warning'
      case 'pending': return 'secondary'
      case 'paid': return 'success'
      default: return 'secondary'
    }
  }

  // Filter data based on search term
  const filteredData = data.filter(milestone =>
    milestone.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.rfq_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.rfq_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    milestone.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="rfq-modal">
      <ModalHeader toggle={toggle}>
        <i className="bx bx-task me-2"></i>
        {title}
        <small className="text-muted ms-2">
          ({pagination.total} total milestones)
        </small>
      </ModalHeader>
      
      <ModalBody>
        {error && (
          <Alert color="danger" className="mb-3">
            <i className="bx bx-error-circle me-2"></i>
            {error}
          </Alert>
        )}

        {/* Search and Export Controls */}
        <Row className="mb-3">
          <Col md={6}>
            <div className="search-box">
              <div className="position-relative">
                <Input
                  type="text"
                  id="search"
                  placeholder="Search by milestone title, RFQ, project, or vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </Col>
          <Col md={2}></Col>
          <Col md={2}>
            <div className="d-flex align-items-center">
              <span className="me-2 text-nowrap">Show:</span>
              <Input
                type="select"
                value={pagination.per_page}
                onChange={(e) => setPagination(prev => ({ ...prev, per_page: parseInt(e.target.value), current_page: 1 }))}
                style={{ fontSize: '0.875rem' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>
            </div>
          </Col>
          <Col md={2} className="text-end">
            <Button
              color="success"
              onClick={handleExport}
              disabled={exporting || loading}
              block
            >
              {exporting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <i className="bx bx-download me-2"></i>
                  Export CSV
                </>
              )}
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading additional milestones...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="table-nowrap table-hover">
                <thead className="table-light">
                  <tr>
                    <th>RFQ#</th>
                    <th>RFQ Title</th>
                    <th>Project</th>
                    <th>Vendor</th>
                    <th>Milestone Title</th>
                    <th>Milestone Amount</th>
                    <th>Status</th>
                    <th>Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((milestone, index) => (
                      <tr key={milestone.id || index}>
                        <td>
                          <strong className="text-primary">{milestone.rfq_code || '-'}</strong>
                        </td>
                        <td>{milestone.rfq_title || '-'}</td>
                        <td>{milestone.project_name || '-'}</td>
                        <td>{milestone.vendor_name || 'Not Assigned'}</td>
                        <td>
                          <strong>{milestone.title || '-'}</strong>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(milestone.payment_amount)}
                          </strong>
                        </td>
                        <td>
                          <Badge color={getStatusBadgeColor(milestone.milestone_status)}>
                            {milestone.milestone_status || 'Pending'}
                          </Badge>
                        </td>
                        <td>{formatDate(milestone.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <i className="bx bx-info-circle display-4 text-muted"></i>
                        <p className="text-muted mt-2">
                          {searchTerm ? 'No milestones match your search criteria' : 'No additional milestones found'}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination - Always show when there's data */}
            {data.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <small className="text-muted">
                    {pagination.total > 0 ? (
                      <>
                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                        {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                        {pagination.total} entries
                      </>
                    ) : (
                      <>Showing {data.length} entries</>
                    )}
                  </small>
                </div>

                {/* Only show pagination controls if there are multiple pages */}
                {pagination.total_pages > 1 && (
                  <div>
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={!pagination.has_prev}
                      className="me-2"
                    >
                      Previous
                    </Button>
                    <span className="mx-2">
                      Page {pagination.current_page} of {pagination.total_pages}
                    </span>
                    <Button
                      color="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={!pagination.has_next}
                      className="ms-2"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}


          </>
        )}
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AdditionalMilestonesModal
